import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, ArrowRight, Loader2, BookOpen } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { bitEvent } from "@/lib/bit";
import { Markdown } from "@/components/Markdown";
import CodeRunner from "@/components/CodeRunner";
import Quiz from "@/components/Quiz";
import ExerciseDeck from "@/components/exercises/ExerciseDeck";
import AdBanner from "@/components/AdBanner";
import { useAuth } from "@/context/AuthContext";

export default function LessonView() {
  const { slug, lessonId } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [course, setCourse] = useState(null);
  const [completed, setCompleted] = useState(new Set());
  const [quizScores, setQuizScores] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [taskPassedMap, setTaskPassedMap] = useState({});
  const [taskJustPassed, setTaskJustPassed] = useState(false);
  const [marking, setMarking] = useState(false);

  const load = () => {
    api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => {});
    api.get("/progress").then((r) => {
      setCompleted(new Set(r.data.completed_lessons));
      setQuizScores(r.data.quiz_scores || {});
      setTaskPassedMap(r.data.task_passed || {});
    }).catch(() => {});
  };

  useEffect(() => {
    load();
    setQuizSubmitted(false);
    setTaskJustPassed(false);
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, lessonId]);

  if (!course) return <div className="font-bold">Lädt...</div>;

  const idx = course.lessons.findIndex((l) => l.id === lessonId);
  const lesson = course.lessons[idx];
  if (!lesson) return <div className="font-bold">Lektion nicht gefunden.</div>;

  const isDone = completed.has(`${slug}/${lessonId}`);
  const next = course.lessons[idx + 1];
  const lessonKey = `${slug}/${lessonId}`;
  const quizAlreadyDone = !lesson.quiz?.length || lesson.quiz.every((q) => quizScores[q.id]);
  const quizGateOpen = quizAlreadyDone || quizSubmitted;
  const taskAlreadyDone = !lesson.task || !!taskPassedMap[lessonKey];
  const taskGateOpen = taskAlreadyDone || taskJustPassed;
  const gateOpen = quizGateOpen && taskGateOpen;
  const gateHint = !quizGateOpen && !taskGateOpen
    ? "Beantworte das Quiz und löse die Aufgabe im Editor"
    : !quizGateOpen
    ? "Beantworte das Quiz"
    : "Löse die Aufgabe im Editor";

  const markComplete = async () => {
    setMarking(true);
    try {
      const { data } = await api.post(`/lessons/${slug}/${lessonId}/complete`);
      if (data.xp_awarded > 0) {
        toast.success(`Lektion abgeschlossen! +${data.xp_awarded} XP`);
        bitEvent("lesson-done", { text: "Lektion abgeschlossen! Die nächste Leiterbahn ist bestromt.", xp: data.xp_awarded });
      } else toast.success("Bereits abgeschlossen");
      setCompleted((prev) => new Set(prev).add(`${slug}/${lessonId}`));
      await refreshUser();
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    } finally {
      setMarking(false);
    }
  };

  const proceedTo = async (destination) => {
    if (!isDone) {
      const ok = await markComplete();
      if (!ok) return;
    }
    navigate(destination);
  };

  return (
    <div className="space-y-6" data-testid="lesson-view">
      <Link to={`/kurs/${slug}`} className="inline-flex items-center gap-1 font-bold text-sm hover:underline" data-testid="back-to-course">
        <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> {course.title}
      </Link>

      <div className="grid lg:grid-cols-12 gap-6 items-start">
        {/* Content */}
        <div className="lg:col-span-5 nb-card p-6">
          <div className="flex items-center gap-2 text-xs font-bold text-muted-foreground mb-2">
            <BookOpen className="h-4 w-4" strokeWidth={2.5} /> Lektion {idx + 1} · {lesson.duration}
          </div>
          <Markdown>{lesson.content}</Markdown>
        </div>

        {/* Editor */}
        <div className="lg:col-span-7 lg:sticky lg:top-20 space-y-4">
          <CodeRunner
            key={lessonId}
            language={lesson.language}
            initialCode={(lesson.task?.starter_code ?? lesson.starter_code) || ""}
            task={lesson.task}
            lessonTitle={lesson.title}
            checkUrl={lesson.task ? `/lessons/${slug}/${lessonId}/check-task` : null}
            taskPassed={taskAlreadyDone}
            onTaskPassed={() => setTaskJustPassed(true)}
          />
        </div>
      </div>

      <ExerciseDeck slug={slug} lessonId={lessonId} />

      {/* Quiz */}
      {lesson.quiz && lesson.quiz.length > 0 && (
        <Quiz key={lessonId} questions={lesson.quiz} alreadyCorrect={quizScores} onGraded={() => setQuizSubmitted(true)} />
      )}

      <AdBanner field="lesson_snippet" className="mx-auto" />

      {/* Complete + Next */}
      {!isDone && !gateOpen && (
        <p className="text-sm font-bold text-muted-foreground" data-testid="quiz-gate-hint">
          {gateHint}, um die Lektion abzuschließen.
        </p>
      )}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        {!next && (
          <button
            onClick={markComplete}
            disabled={marking || (!isDone && !gateOpen)}
            title={!isDone && !gateOpen ? gateHint : undefined}
            data-testid="lesson-complete-button"
            className={`flex-1 flex items-center justify-center gap-2 font-bold py-3.5 rounded-xl border-2 border-black nb-shadow nb-press ${
              isDone ? "bg-success text-black" : "bg-primary text-white"
            } disabled:opacity-60`}
          >
            {marking ? <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2.5} /> : <CheckCircle2 className="h-5 w-5" strokeWidth={2.5} />}
            {isDone ? "Abgeschlossen" : "Lektion abschließen"}
          </button>
        )}
        {next ? (
          <button
            onClick={() => proceedTo(`/kurs/${slug}/lektion/${next.id}`)}
            disabled={marking || (!isDone && !gateOpen)}
            title={!isDone && !gateOpen ? gateHint : undefined}
            data-testid="next-lesson-button"
            className="flex-1 flex items-center justify-center gap-2 bg-secondary text-white font-bold py-3.5 rounded-xl border-2 border-black nb-shadow nb-press disabled:opacity-60"
          >
            {marking ? <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2.5} /> : null}
            Nächste Lektion
            <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
          </button>
        ) : (
          <button
            onClick={() => proceedTo(`/kurs/${slug}`)}
            disabled={marking || (!isDone && !gateOpen)}
            title={!isDone && !gateOpen ? gateHint : undefined}
            data-testid="finish-course-button"
            className="flex-1 flex items-center justify-center gap-2 bg-black text-white font-bold py-3.5 rounded-xl border-2 border-black nb-shadow nb-press disabled:opacity-60"
          >
            {marking ? <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2.5} /> : null}
            Kurs-Übersicht
            <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
          </button>
        )}
      </div>
    </div>
  );
}
